"use client";

import type { User } from "@supabase/supabase-js";
import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { Suspense, useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { createClient } from "@/lib/supabase/client";
import { identify } from "@/lib/analytics/events";
import { testIdentityProps } from "@/lib/analytics/internal-user";
import { onConsentChange, readConsentSync } from "@/lib/consent";
import { scrubUrlProperties } from "@/lib/analytics-scrub";

const KEY = process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN;
// First-party reverse proxy (rewrite in next.config.ts) so ad blockers can't
// drop our analytics: posthog-js served from eu-assets.i.posthog.com is blocked
// outright by uBlock / EasyPrivacy / Brave, and those visitors were never
// counted. Deliberately NOT read from NEXT_PUBLIC_POSTHOG_HOST — a stale env
// var pointing at the third-party host silently undoes the proxy. To retarget,
// change the rewrite destination instead.
const HOST = "/relay-fsx";

// ── Initialise once, at MODULE LOAD (not inside a useEffect) ─────────────────
// React runs child effects before parent effects, so a $pageview fired from the
// PageviewTracker child would land before a useEffect-based init completed — and
// posthog-js silently drops captures made before .init() runs. Initialising at
// module level is the pattern PostHog documents for Next.js App Router.
//
// TTDSG §25 + GDPR, two-tier by design:
//   DEFAULT (no consent decision yet) — BASIC, COOKIELESS, ANONYMOUS reach:
//     persistence:"memory" + disable_persistence:true write NOTHING to the
//     device, so §25 is not triggered and no consent is required. Page views +
//     visitor counts are lawful under Art. 6(1)(f) legitimate interest,
//     minimised: EU-hosted, no persistent cross-session id, replay OFF. So we
//     capture by DEFAULT — no opt_out_capturing_by_default, no respect_dnt.
//   ON "Accept" (CookieConsent) — PERSISTENT upgrade:
//     persistence → "localStorage+cookie" (cross-session identity + funnel
//     tracing) + session recording on. THAT device storage is what §25 consent
//     authorises.
//   ON "Decline" — opt out of everything (handled just below + in the banner).
//
// Reuses the SAME consent state as the rest of the app (fc.consent.v1 via
// lib/consent.ts) — no new cookie/flag is invented here.
// Portfolio play: NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN is the same value in every
// environment (.env.example ships one slot, not a dev/prod pair), so `pnpm dev`
// on a contributor's machine sends real events into this product's live
// PostHog project under the real token. devotionfund measured this at 26% of
// 30-day pageviews being local-dev traffic, silently inflating every
// traffic-derived decision. Skip init for localhost/127.0.0.1/*.localhost.
function isLocalDevHost(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]" ||
    hostname.endsWith(".localhost")
  );
}

// Portfolio play: automated sessions (headless/driven browsers used for prod
// verification, uptime checks, etc.) set navigator.webdriver=true and would
// otherwise self-pollute visitor counts every time a deploy is verified live.
// Skip init entirely for them rather than filtering after the fact.
//
// The Control Center's own ConceptZoneProbe uptime monitor runs with
// navigator.webdriver suppressed, so it slips past that check alone. IMPORTANT:
// do NOT also skip init for it (a prior version of this file did, in
// e6de98d) — the "analytics-probe-financecareers" heartbeat reads THIS SAME
// prober's own PostHog events as its liveness signal, so fully excluding it
// from capture doesn't just clean up visitor counts, it silences the heartbeat
// and fires a false DOWN alert (T-258, ~12.6h after that commit landed — the
// timing lines up exactly). Tag it via before_send instead, so the event still
// reaches PostHog and funnel/visitor queries can filter `is_uptime_prober`.
function isConceptZoneProbe(userAgent: string): boolean {
  return userAgent.includes("ConceptZoneProbe");
}

if (
  typeof window !== "undefined" &&
  KEY &&
  !posthog.__loaded &&
  !navigator.webdriver &&
  !isLocalDevHost(window.location.hostname)
) {
  const consent = readConsentSync(); // "granted" | "denied" | null

  posthog.init(KEY, {
    // Auto-capture uncaught client-side exceptions (window.onerror +
    // unhandledrejection). There is no Sentry anywhere in this portfolio (that
    // decision is recorded in docs/portfolio-error-observability-decision), so
    // without this a page that crashes for a real user reports nothing at all
    // and the first symptom is the user leaving. The project-level
    // autocapture_exceptions_opt_in flag is NOT enough on its own: devotionfund
    // has it set and has captured zero, because its SDK init lacked this line.
    // Free at our volume — PostHog includes 100k exceptions/month.
    capture_exceptions: true,
    api_host: HOST,
    ui_host: "https://eu.posthog.com",
    capture_pageview: true, // basic reach on every load; SPA nav handled below
    capture_pageleave: true,
    // Portfolio play: a Netlify/Next rewrite proxy can corrupt gzip-js POST
    // bodies (400s, silent capture loss). We proxy through a Next rewrite too
    // (see HOST above) — disabling compression removes that failure mode for
    // the cost of a slightly larger payload.
    disable_compression: true,
    // Override posthog-js's identified_only default: its capture() gate
    // short-circuits (silent early-return, no POST) for anonymous visitors
    // unless person profiles are allowed. "always" makes anonymous pageviews +
    // autocapture actually fire — required for cookieless reach measurement.
    person_profiles: "always",
    // Pre-consent tier is cookieless: "memory" + disable_persistence:true means
    // posthog writes NOTHING to the device (ephemeral in-memory distinct_id per
    // load). On "Accept" the banner flips these to localStorage+cookie / false.
    persistence: consent === "granted" ? "localStorage+cookie" : "memory",
    disable_persistence: consent !== "granted",
    disable_session_recording: consent !== "granted",
    // opt_out_capturing() below only stops EVENT capture — posthog-js still
    // auto-fetches feature flags on load/identify regardless (a real leak:
    // TSF-23 on thesportfund found a declined visitor's browser still POSTing
    // to /relay-fsx/flags/). This product has zero useFeatureFlag/getFeatureFlag
    // call sites today, so nothing breaks by disabling it for the non-granted
    // tier; re-enabled + reloaded on "Accept" below.
    advanced_disable_feature_flags_on_first_load: consent !== "granted",
    // Data minimisation: strip query strings (search terms/filters) from every
    // URL property before it is sent.
    // Data minimisation: strip query strings from every URL-ish property.
    // Pure and tested in src/lib/analytics-scrub.ts — the test runs
    // the scrubber against a real tokenised preferences link rather than
    // grepping for it.
    sanitize_properties: (props) => scrubUrlProperties(props),
    // Tag every event captured on a 404 load with page_status:"not_found" so
    // funnel/engagement metrics can filter out dead-link and scraper traffic
    // instead of counting it as real engagement. The meta tag comes from
    // app/not-found.tsx; absent on every real page, so this is a no-op there.
    before_send: (event) => {
      if (!event) return event;
      const status = document
        .querySelector('meta[name="page-status"]')
        ?.getAttribute("content");
      if (status) event.properties = { ...event.properties, page_status: status };
      if (isConceptZoneProbe(navigator.userAgent)) {
        event.properties = { ...event.properties, is_uptime_prober: true };
      }
      return event;
    },
  });

  if (consent === "denied") {
    // Honour an explicit opt-out: stop all capture + wipe any stale storage a
    // previous visit may have left on the device.
    posthog.opt_out_capturing();
    posthog.reset(true);
    // Belt-and-suspenders: advanced_disable_feature_flags_on_first_load above
    // only skips the INITIAL fetch; this stops any later automatic reload too.
    posthog.featureFlags.setReloadingPaused(true);
  }

  // Expose for debugging in dev consoles.
  (window as unknown as { posthog: typeof posthog }).posthog = posthog;
}

/**
 * Attribution caveat (confirmed live 2026-09-09): because pre-consent visitors
 * get persistence:"memory" (a fresh distinct_id every load), person-level
 * "$initial_referring_domain" never survives to a second pageview and reads
 * null/"$direct" for ~100% of the last 30d (5647/5647 pageviews checked). The
 * PER-EVENT "$referring_domain" is unaffected (0/5647 null) — it's just
 * document.referrer on that one capture, not identity-dependent. Any future
 * acquisition/channel analysis (including PostHog's own Web Analytics
 * dashboard, which defaults to initial-touch) MUST use $referring_domain per
 * event, never $initial_referring_domain, or it will silently misattribute
 * nearly all cookieless traffic as direct/unknown.
 */

/**
 * PostHog is initialised at module load and captures anonymous, cookieless
 * page views for EVERY visitor by default (basic reach — no consent needed).
 * The cookie banner only decides the PERSISTENT tier: "Accept" upgrades to
 * on-device cookies + session replay; "Decline" opts analytics out entirely.
 */
export function PostHogProvider({ children }: { children: React.ReactNode }) {
  // Apply consent decisions that happen AFTER init (e.g. reopening the banner
  // from the footer, or a choice made later in the session). The banner itself
  // also calls posthog directly for immediate effect; this keeps the two in
  // sync and covers the case where posthog wasn't loaded when the banner ran.
  useEffect(() => {
    if (!KEY || !posthog.__loaded) return;
    return onConsentChange((v) => {
      if (v === "granted") {
        posthog.set_config({
          persistence: "localStorage+cookie",
          disable_persistence: false,
        });
        posthog.opt_in_capturing();
        posthog.startSessionRecording?.();
        posthog.featureFlags.setReloadingPaused(false);
        posthog.reloadFeatureFlags();
      } else if (v === "denied") {
        posthog.opt_out_capturing();
        posthog.reset(true);
        posthog.featureFlags.setReloadingPaused(true);
      }
      // v === null (banner reopened): leave current capture running until the
      // visitor makes a choice — basic cookieless reach continues meanwhile.
    });
  }, []);

  if (!KEY) return <>{children}</>;
  return (
    <PHProvider client={posthog}>
      <Suspense fallback={null}>
        <PageviewTracker />
      </Suspense>
      <AuthIdentify />
      {children}
    </PHProvider>
  );
}

/**
 * Link PostHog events to the signed-in account (CM-10). identify() on session
 * restore / sign-in attributes events to the auth user; reset() on sign-out
 * unlinks the device. Guarded on opt-in so a declined visitor is untouched.
 */
function identifyUser(user: User): void {
  identify(user.id, {
    email: user.email,
    signup_date: user.created_at,
    // Portfolio play (proven on talentists-tutor): @concept.zone is the
    // portfolio-wide executor/QA domain, so a session identified under it is
    // never a real candidate — tag it so signup/activation metrics can
    // exclude test logins instead of counting them as growth.
    is_internal_qa: user.email?.endsWith("@concept.zone") ?? false,
    // What the test-account cohort actually matches (conceptzonetech+ has no
    // @concept.zone to catch) — so our own logins drop out of every number.
    ...testIdentityProps(user.email),
  });
}

function AuthIdentify() {
  useEffect(() => {
    if (!KEY) return;
    const supabase = createClient();
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (!posthog.__loaded) return;
      if (event === "SIGNED_OUT") {
        posthog.reset();
        return;
      }
      if (session?.user) identifyUser(session.user);
    });
    return () => sub.subscription.unsubscribe();
  }, []);
  return null;
}

function PageviewTracker() {
  const pathname = usePathname();
  // An effect keyed on `pathname` also fires on the component's FIRST mount,
  // not just on later changes — so without this guard, every hard page load
  // double-captured $pageview (once from posthog.init's own capture_pageview,
  // once here). Found 2026-09-07 via same-session, sub-second-apart duplicate
  // $pageview timestamps on ~45% of session/path pairs (checked 2026-09-05's
  // full day). Skip exactly one fire per mount so only real client-side route
  // changes reach this capture call.
  const skippedFirst = useRef(false);
  useEffect(() => {
    if (!KEY || !pathname || !posthog.__loaded) return;
    if (!skippedFirst.current) {
      skippedFirst.current = true;
      return;
    }
    // Path only — query strings (search terms/filters) are never sent.
    posthog.capture("$pageview", { $current_url: window.origin + pathname });
  }, [pathname]);
  return null;
}
